/**
 * UsersTab.js
 * ============
 * Manage users with access to a property
 * Supports: Owner, Family Member, Tenant, Visitor roles
 */

import React, { useState, useEffect } from "react";
import {
  Users,
  UserPlus,
  Crown,
  Home,
  Key,
  Eye,
  Mail,
  Phone,
  X,
  Check,
  Trash2,
  Shield,
  Clock,
  Info,
} from "lucide-react";
import TabLayout from "./Shared/TabLayout.jsx";
import { useAuth } from "../context/AuthContext.jsx";

const ROLE_CONFIG = {
  owner: {
    label: "Owner",
    icon: Crown,
    color: "text-amber-600",
    bgColor: "bg-amber-50",
    borderColor: "border-amber-200",
    description: "Full access to property"
  },
  family_member: {
    label: "Family Member",
    icon: Home,
    color: "text-blue-600",
    bgColor: "bg-blue-50",
    borderColor: "border-blue-200",
    description: "Can view and edit, manage finances"
  },
  tenant: {
    label: "Tenant",
    icon: Key,
    color: "text-green-600",
    bgColor: "bg-green-50",
    borderColor: "border-green-200",
    description: "Can report issues and view unit info"
  },
  visitor: {
    label: "Visitor",
    icon: Eye,
    color: "text-gray-600",
    bgColor: "bg-gray-50",
    borderColor: "border-gray-200",
    description: "Can report issues only"
  }
};

export default function UsersTab({ property }) {
  const { getToken } = useAuth();
  const authHeaders = (extra = {}) => ({
    Authorization: `Bearer ${getToken()}`,
    ...extra,
  });
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteForm, setInviteForm] = useState({
    name: "",
    email: "",
    phone: "",
    role: "tenant"
  });
  const [inviting, setInviting] = useState(false);
  const [error, setError] = useState("");

  const propertyId = property?.id;

  // Fetch users for this property
  useEffect(() => {
    if (!propertyId) return;

    async function fetchUsers() {
      try {
        setLoading(true);
        const res = await fetch(`/api/properties/${propertyId}/users`, {
          headers: authHeaders(),
        });
        if (!res.ok) throw new Error("Failed to load users");
        const data = await res.json();
        setUsers(data);
      } catch (err) {
        console.error(err);
        setError("Could not load users");
      } finally {
        setLoading(false);
      }
    }

    fetchUsers();
  }, [propertyId, getToken]);

  // Invite a new user
  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteForm.name || !inviteForm.role) return;

    setInviting(true);
    setError("");

    try {
      const res = await fetch(`/api/properties/${propertyId}/users/invite`, {
        method: "POST",
        headers: authHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify(inviteForm)
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || "Failed to invite user");
      }

      const newUser = await res.json();
      setUsers((prev) => [...prev, newUser]);
      setShowInviteModal(false);
      setInviteForm({ name: "", email: "", phone: "", role: "tenant" });
    } catch (err) {
      setError(err.message);
    } finally {
      setInviting(false);
    }
  };

  // Remove user from property
  const handleRemoveUser = async (userId) => {
    if (!window.confirm("Remove this user's access to the property?")) return;

    try {
      const res = await fetch(`/api/properties/${propertyId}/users/${userId}`, {
        method: "DELETE",
        headers: authHeaders(),
      });

      if (!res.ok) throw new Error("Failed to remove user");

      setUsers((prev) => prev.filter((u) => u.userId !== userId));
    } catch (err) {
      setError(err.message);
    }
  };

  // Update user role
  const handleUpdateRole = async (userId, newRole) => {
    try {
      const res = await fetch(`/api/properties/${propertyId}/users/${userId}`, {
        method: "PUT",
        headers: authHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({ role: newRole })
      });

      if (!res.ok) throw new Error("Failed to update role");

      const updated = await res.json();
      setUsers((prev) =>
        prev.map((u) => (u.userId === userId ? updated : u))
      );
    } catch (err) {
      setError(err.message);
    }
  };

  // Count users by role
  const usersByRole = users.reduce((acc, u) => {
    acc[u.role] = (acc[u.role] || 0) + 1;
    return acc;
  }, {});

  // Sidebar content
  const usersSidebar = (
    <div className="space-y-6">
      {/* User Summary */}
      <div>
        <h3 className="font-semibold text-[#262626] mb-3 flex items-center gap-2">
          <Users className="w-4 h-4 text-[#CC0202]" />
          User Summary
        </h3>
        <div className="p-4 bg-gray-50 rounded-lg space-y-2">
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600">Total Users</span>
            <span className="font-bold text-[#262626]">{users.length}</span>
          </div>
          {Object.entries(usersByRole).map(([role, count]) => {
            const config = ROLE_CONFIG[role];
            return (
              <div key={role} className="flex justify-between items-center">
                <span className={`text-sm ${config?.color || "text-gray-600"}`}>
                  {config?.label || role}
                </span>
                <span className="text-sm font-medium">{count}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Role Legend */}
      <div>
        <h3 className="font-semibold text-[#262626] mb-3 flex items-center gap-2">
          <Shield className="w-4 h-4 text-[#CC0202]" />
          Role Permissions
        </h3>
        <div className="space-y-3">
          {Object.entries(ROLE_CONFIG).map(([key, config]) => {
            const RoleIcon = config.icon;
            return (
              <div
                key={key}
                className={`p-3 rounded-lg border ${config.borderColor} ${config.bgColor}`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <RoleIcon className={`w-4 h-4 ${config.color}`} />
                  <span className={`font-medium text-sm ${config.color}`}>
                    {config.label}
                  </span>
                </div>
                <p className="text-xs text-gray-600">{config.description}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Quick Actions */}
      <div>
        <h3 className="font-semibold text-[#262626] mb-3 flex items-center gap-2">
          <Info className="w-4 h-4 text-[#CC0202]" />
          Quick Tips
        </h3>
        <div className="space-y-2 text-xs text-gray-600">
          <p className="flex items-start gap-2">
            <span className="text-[#CC0202]">•</span>
            Owners have full control over all property settings
          </p>
          <p className="flex items-start gap-2">
            <span className="text-[#CC0202]">•</span>
            Family members can manage finances and documents
          </p>
          <p className="flex items-start gap-2">
            <span className="text-[#CC0202]">•</span>
            Tenants can report issues and view their unit info
          </p>
          <p className="flex items-start gap-2">
            <span className="text-[#CC0202]">•</span>
            Visitors have read-only access to report issues
          </p>
        </div>
      </div>

      {/* Recent Activity Placeholder */}
      <div>
        <h3 className="font-semibold text-[#262626] mb-3 flex items-center gap-2">
          <Clock className="w-4 h-4 text-[#CC0202]" />
          Recent Activity
        </h3>
        <div className="text-xs text-gray-500 text-center py-4 bg-gray-50 rounded-lg">
          No recent activity
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="p-6 animate-pulse">
        <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
        <div className="space-y-3">
          <div className="h-20 bg-gray-200 rounded"></div>
          <div className="h-20 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <TabLayout
      sidebar={usersSidebar}
      sidebarTitle="Users & Permissions"
      storageKey="users-sidebar"
    >
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Users className="w-6 h-6 text-hn-red" />
          <h2 className="text-xl font-bold text-hn-dark">Property Users</h2>
          <span className="text-sm text-gray-500">
            {users.length} {users.length === 1 ? "person" : "people"}
          </span>
        </div>

        <button
          onClick={() => setShowInviteModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-hn-red text-white rounded-lg hover:bg-red-700 transition"
        >
          <UserPlus className="w-4 h-4" />
          Add User
        </button>
      </div>

      {error && (
        <div className="p-3 bg-red-50 text-red-600 rounded-lg text-sm">
          {error}
        </div>
      )}

      {/* Users List */}
      <div className="space-y-3">
        {users.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>No users added yet</p>
            <p className="text-sm">Add owners, tenants, or family members</p>
          </div>
        ) : (
          users.map((pu) => {
            const roleConfig = ROLE_CONFIG[pu.role] || ROLE_CONFIG.visitor;
            const RoleIcon = roleConfig.icon;

            return (
              <div
                key={pu.id}
                className={`p-4 rounded-xl border ${roleConfig.borderColor} ${roleConfig.bgColor} flex items-center justify-between`}
              >
                <div className="flex items-center gap-4">
                  {/* Avatar */}
                  <div className="w-12 h-12 rounded-full bg-white border-2 border-gray-200 flex items-center justify-center text-lg font-bold text-gray-600">
                    {pu.user?.name?.charAt(0)?.toUpperCase() || "?"}
                  </div>

                  {/* User Info */}
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-hn-dark">
                        {pu.user?.name || "Unknown"}
                      </span>
                      <span
                        className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${roleConfig.bgColor} ${roleConfig.color} border ${roleConfig.borderColor}`}
                      >
                        <RoleIcon className="w-3 h-3" />
                        {roleConfig.label}
                      </span>
                    </div>

                    <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
                      {pu.user?.email && (
                        <span className="flex items-center gap-1">
                          <Mail className="w-3 h-3" />
                          {pu.user.email}
                        </span>
                      )}
                      {pu.user?.phone && (
                        <span className="flex items-center gap-1">
                          <Phone className="w-3 h-3" />
                          {pu.user.phone}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2">
                  <select
                    value={pu.role}
                    onChange={(e) => handleUpdateRole(pu.userId, e.target.value)}
                    className="text-sm border rounded-lg px-2 py-1 bg-white"
                  >
                    {Object.entries(ROLE_CONFIG).map(([key, config]) => (
                      <option key={key} value={key}>
                        {config.label}
                      </option>
                    ))}
                  </select>

                  <button
                    onClick={() => handleRemoveUser(pu.userId)}
                    className="p-2 text-gray-400 hover:text-red-500 transition"
                    title="Remove user"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md mx-4 overflow-hidden">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-bold text-hn-dark">Add User</h3>
              <button
                onClick={() => setShowInviteModal(false)}
                className="p-1 hover:bg-gray-100 rounded-full transition"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <form onSubmit={handleInvite} className="p-4 space-y-4">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Full Name *
                </label>
                <input
                  type="text"
                  value={inviteForm.name}
                  onChange={(e) =>
                    setInviteForm({ ...inviteForm, name: e.target.value })
                  }
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-hn-red focus:border-transparent"
                  placeholder="John Doe"
                  required
                />
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={inviteForm.email}
                  onChange={(e) =>
                    setInviteForm({ ...inviteForm, email: e.target.value })
                  }
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-hn-red focus:border-transparent"
                  placeholder="john@example.com"
                />
              </div>

              {/* Phone */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone
                </label>
                <input
                  type="tel"
                  value={inviteForm.phone}
                  onChange={(e) =>
                    setInviteForm({ ...inviteForm, phone: e.target.value })
                  }
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-hn-red focus:border-transparent"
                  placeholder="+1 416 555 1234"
                />
              </div>

              {/* Role */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Role *
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {Object.entries(ROLE_CONFIG).map(([key, config]) => {
                    const RoleIcon = config.icon;
                    const isSelected = inviteForm.role === key;

                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() =>
                          setInviteForm({ ...inviteForm, role: key })
                        }
                        className={`p-3 rounded-lg border-2 text-left transition ${
                          isSelected
                            ? `${config.borderColor} ${config.bgColor}`
                            : "border-gray-200 hover:border-gray-300"
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <RoleIcon
                            className={`w-4 h-4 ${
                              isSelected ? config.color : "text-gray-400"
                            }`}
                          />
                          <span
                            className={`font-medium ${
                              isSelected ? config.color : "text-gray-600"
                            }`}
                          >
                            {config.label}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          {config.description}
                        </p>
                      </button>
                    );
                  })}
                </div>
              </div>

              {error && (
                <div className="p-3 bg-red-50 text-red-600 rounded-lg text-sm">
                  {error}
                </div>
              )}

              {/* Submit */}
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowInviteModal(false)}
                  className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={inviting || !inviteForm.name}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-hn-red text-white rounded-lg hover:bg-red-700 disabled:bg-gray-300 transition"
                >
                  {inviting ? (
                    "Adding..."
                  ) : (
                    <>
                      <Check className="w-4 h-4" />
                      Add User
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
    </TabLayout>
  );
}
